import type { ColumnDef } from "@tanstack/react-table";
import { DifferentiationResult } from "@/services/backend/differentiation/differentiation";
import { useListResultsQuery } from "@/services/backend/differentiation/service";
import { Procedure } from "@/services/backend/procedures/procedure/procedure";
import { Badge } from "@/shared/components/ui/badge";
import { RTKQueryErrorAlert } from "@/shared/components/domain/errors/rtk-query-error-alert";
import { formatDate } from "@/shared/utilities/date";
import {
  DataTable,
  DataTableContainer,
  useDataTableController,
} from "../../data-table";

interface MouldResultTableProps {
  procedure: Procedure;
}

const tableColumns: ColumnDef<DifferentiationResult>[] = [
  {
    id: "description",
    accessorKey: "description",
    header: "Beschreibung",
    cell: ({ row }) => {
      const differentiationData = row.original;
      return <span>{differentiationData.mould?.description}</span>;
    },
  },
  {
    id: "value",
    accessorKey: "value",
    header: "Zählung",
    cell: ({ row }) => {
      const differentiationData = row.original;
      if (differentiationData.value) {
        return <span>{differentiationData.value}</span>;
      }

      return differentiationData.exceedsThreshold ? (
        <span>{">100"}</span>
      ) : (
        <span>Kein Wert vorhanden</span>
      );
    },
  },
  {
    id: "estimate",
    accessorKey: "estimate",
    header: "Abschätzung",
    cell: ({ row }) => {
      const differentiationData = row.original;
      return differentiationData.estimate ? (
        <span>{differentiationData.estimate}</span>
      ) : (
        <span>Keine Abschätzung vorhanden</span>
      );
    },
  },
  {
    id: "overgrown",
    accessorKey: "overgrown",
    header: "Überwachsen",
    cell: ({ row }) => {
      const differentiationData = row.original;
      if (differentiationData.overgrown) {
        return <Badge className="bg-red-700 text-white">Ja</Badge>;
      }
      return <Badge className="bg-green-700 text-white">Nein</Badge>;
    },
  },
  {
    id: "createdAt",
    accessorKey: "createdAt",
    header: "Angelegt am",
    cell: ({ row }) => {
      const differentiationData = row.original;
      return <span>{formatDate(differentiationData.createdAt!)}</span>;
    },
  },
  {
    id: "updatedAt",
    accessorKey: "updatedAt",
    header: "Aktualisiert am",
    cell: ({ row }) => {
      const differentiationData = row.original;
      return <span>{formatDate(differentiationData.updatedAt!)}</span>;
    },
  },
];

export function MouldResultTable({ procedure }: MouldResultTableProps) {
  const {
    data: list,
    error,
    isLoading,
    isFetching,
  } = useListResultsQuery({
    sampleSetId: {
      active: true,
      values: [procedure.sampleSetId],
    },
    limit: 0,
    offset: 0,
  });

  const table = useDataTableController(tableColumns, list?.items || []);

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return (
    <div className="flex w-full flex-col space-y-2">
      <DataTableContainer className="bg-white">
        <DataTable
          table={table}
          columns={tableColumns}
          isLoading={isLoading || isFetching}
        />
      </DataTableContainer>
    </div>
  );
}
